import { useState, type ReactNode } from 'react';

import type { FeedHealthStatus, SubscriptionSummary } from '../api/types.js';
import type { TimelineScope } from '../state/timeline.js';

/**
 * The subscription sidebar (M1.15.1, M2.6.1).
 *
 * ## Counts come from the server, always
 *
 * Every number here is one the server sent — from `GET /counts`, or from the
 * `StateChangeResult` a mutation returned. **Nothing is recomputed from the
 * items on screen.** D2 makes the server authoritative, and a client that
 * tallies its own list is wrong the moment an unread item is outside the
 * loaded page, which for any real feed list is immediately. A folder's number
 * is the sum of its feeds' server-issued counts, including those in folders
 * nested inside it.
 *
 * ## Counts are hidden unless Inbox mode is on
 *
 * [D18](../../../Design-Decisions.md) and M1.18.1. The numbers are fetched and
 * held either way — turning the mode on is instant and shows real counts
 * rather than counts that start from the moment you asked.
 *
 * ## Folders nest, because the API has let them since M2.1.1
 *
 * Five levels deep, and this renders the actual tree. A flat list of groups
 * would make "drag a folder into another folder" a change with no visible
 * result, and an OPML import from somebody with nested folders would silently
 * flatten on screen while being correct in the database.
 *
 * ## Dragging, and what it is not
 *
 * A feed can be dragged onto a folder to move it, and a folder onto another to
 * nest it. **Dragging is not the only way to do either**, deliberately: a drag
 * is unreachable from a keyboard and awkward on a touchscreen, and
 * reorganising a feed list is exactly the task somebody would want to do with
 * neither a mouse nor precision. So every drop has a control that does the
 * same thing — a "move" button on each feed, and a parent picker in the folder
 * dialog.
 *
 * ## The order within a folder is the reader's (M2.1.3)
 *
 * Feeds sort by `position` and fall back to their title, so a list nobody has
 * touched is alphabetical and one they have arranged stays arranged. Dropping
 * a feed *onto another feed* places it there; dropping it onto a **folder**
 * moves it in. Both are one drag and the difference is where the cursor
 * lands, which is what every file manager has taught people — so the two
 * highlight differently, a leading edge against a filled row.
 *
 * ## A broken feed and a quiet feed look different (M2.4.3)
 *
 * The distinction the whole health feature exists for, and the sidebar is
 * where somebody notices. A feed that is failing carries a marker; **a feed
 * that has simply published nothing carries none**, because it is not a
 * problem and flagging it would tell the reader their taste in writers is a
 * fault. The three unhealthy states are drawn differently from each other
 * too — a publisher restarting a server should not look like a withdrawn
 * feed — and every marker's tooltip is the server's own sentence, so the
 * wording lives in one place rather than being reinvented per client.
 *
 * ## Collapsed folders are remembered per viewer
 *
 * Locally, because it is a display preference about this screen on this
 * device. Somebody with forty feeds collapses them once and expects that to
 * hold; making it a server round trip would be a write on every triangle.
 */

export interface Folder {
  readonly folderId: string;
  readonly name: string;
  readonly parentId?: string | null;
  readonly position?: number | null;
}

/** What is being dragged, if anything. */
type Dragged =
  | { readonly kind: 'feed'; readonly id: string }
  | { readonly kind: 'folder'; readonly id: string };

export interface SidebarProps {
  readonly subscriptions: readonly SubscriptionSummary[];
  readonly folders: readonly Folder[];
  /** Server-issued counts, keyed by subscription id. */
  readonly counts: ReadonlyMap<string, number>;
  /**
   * Server-issued folder counts, keyed by folder id, each already including
   * the folders nested inside it (M2.1.3).
   */
  readonly folderCounts?: ReadonlyMap<string, number> | undefined;
  readonly total: number;
  readonly scope: TimelineScope;
  /** The account's tags, for the Tags section (M4.2.3). */
  readonly tags?:
    readonly { readonly name: string; readonly count: number }[] | undefined;
  readonly showCounts: boolean;
  readonly collapsed: readonly string[];
  readonly onSelect: (scope: TimelineScope) => void;
  readonly onToggleFolder: (folderId: string) => void;
  readonly onAddFeed: () => void;
  /** Opens the import screen (M2.6.3). */
  readonly onImport?: (() => void) | undefined;
  /** Removes a subscription (M2.5.1). Confirmed by the caller. */
  readonly onUnsubscribe?: ((feedId: string, title: string) => void) | undefined;
  readonly onCreateFolder?: (() => void) | undefined;
  readonly onRenameFolder?: ((folderId: string) => void) | undefined;
  readonly onDeleteFolder?: ((folderId: string) => void) | undefined;
  /** Moves a feed into a folder, or out of all of them with `null`. */
  readonly onMoveFeed?: ((feedId: string, folderId: string | null) => void) | undefined;
  /** Opens the keyboard-reachable equivalent of a drag. */
  readonly onMoveFeedDialog?: ((feedId: string) => void) | undefined;
  /** Nests a folder inside another, or at the top with `null`. */
  readonly onMoveFolder?:
    ((folderId: string, parentId: string | null) => void) | undefined;
  /**
   * Places one feed immediately before another (M2.1.3).
   *
   * `before` is `undefined` for a drop at the end of a folder. The caller
   * works out the position — the sidebar knows the order on screen, not how
   * positions are numbered.
   */
  readonly onReorderFeed?:
    | ((feedId: string, folderId: string | null, before: string | undefined) => void)
    | undefined;
}

/** The synthetic group for subscriptions in no folder at all. */
const UNFILED = '__unfiled__';

export function Sidebar(props: SidebarProps): ReactNode {
  const [dragged, setDragged] = useState<Dragged | undefined>(undefined);
  const [over, setOver] = useState<string | undefined>(undefined);

  /*
   * Picking a feed or folder from the Starred view goes back to reading
   * feeds: starred is a listing of its own, and "starred items in this feed"
   * is not what a click on a feed means.
   */
  const feedFilter = props.scope.filter === 'starred' ? 'unread' : props.scope.filter;
  const inFeeds = props.scope.filter !== 'starred' && props.scope.tag === undefined;

  const childrenOf = (parentId: string | undefined): Folder[] =>
    props.folders
      .filter((folder) => (folder.parentId ?? undefined) === parentId)
      .sort(byPositionThenName);

  /**
   * The feeds in one folder, in the order the reader put them (M2.1.3).
   *
   * Position first, title where there is none — so a list nobody has
   * reordered is alphabetical, one they have arranged stays arranged, and a
   * feed added afterwards sorts to the end, which is where a new thing
   * belongs.
   */
  const feedsIn = (folderId: string | undefined): SubscriptionSummary[] =>
    props.subscriptions
      .filter((subscription) => (subscription.folderId ?? undefined) === folderId)
      .sort((left, right) => {
        const a = left.position ?? Number.MAX_SAFE_INTEGER;
        const b = right.position ?? Number.MAX_SAFE_INTEGER;
        return a === b ? left.title.localeCompare(right.title) : a - b;
      });

  const unreadFor = (subscription: SubscriptionSummary): number =>
    props.counts.get(subscription.feedId) ?? subscription.unread;

  /**
   * A folder's unread, including the folders inside it.
   *
   * **The server's number where there is one** (M2.1.3): `GET /counts` rolls
   * a folder's subtree up, and D2 makes it authoritative. The local sum is a
   * fallback for two cases — a server that predates the roll-up, and the
   * moment after a mutation, when the per-feed numbers it returned are
   * fresher than any folder total.
   */
  const subtreeUnread = (folderId: string): number =>
    props.folderCounts?.get(folderId) ??
    feedsIn(folderId).reduce((sum, feed) => sum + unreadFor(feed), 0) +
      childrenOf(folderId).reduce((sum, child) => sum + subtreeUnread(child.folderId), 0);

  /* --------------------------------------------------------- dragging --- */

  const canDrop = (target: string): boolean => {
    if (dragged === undefined) {
      return false;
    }
    if (dragged.kind === 'feed') {
      return true;
    }
    // A folder cannot be dropped into itself or into its own subtree; the API
    // refuses both, and an accepting drop target that then errors is worse
    // than one that never highlighted.
    return (
      dragged.id !== target &&
      (target === UNFILED || !isWithin(target, dragged.id, props.folders))
    );
  };

  const drop = (target: string): void => {
    if (dragged === undefined || !canDrop(target)) {
      return;
    }
    const parentId = target === UNFILED ? null : target;

    if (dragged.kind === 'feed') {
      props.onMoveFeed?.(dragged.id, parentId);
    } else {
      props.onMoveFolder?.(dragged.id, parentId);
    }

    setDragged(undefined);
    setOver(undefined);
  };

  const dropProps = (target: string): Record<string, unknown> => ({
    onDragOver: (event: React.DragEvent) => {
      if (canDrop(target)) {
        // Without this the browser refuses the drop, silently.
        event.preventDefault();
        setOver(target);
      }
    },
    onDragLeave: () => {
      setOver((current) => (current === target ? undefined : current));
    },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      drop(target);
    },
  });

  /* ----------------------------------------------------------- render --- */

  /**
   * Dropping a feed *onto another feed* means "put it here" (M2.1.3).
   *
   * Distinct from dropping on a folder, which means "put it in there". Both
   * gestures are one drag and the difference is where the cursor lands, which
   * is the convention every file manager has taught people — so the row
   * highlights on its leading edge rather than filling in like a folder does.
   */
  const reorderProps = (subscription: SubscriptionSummary): Record<string, unknown> => ({
    onDragOver: (event: React.DragEvent) => {
      if (
        dragged?.kind === 'feed' &&
        dragged.id !== subscription.feedId &&
        props.onReorderFeed !== undefined
      ) {
        event.preventDefault();
        // Stops the enclosing folder claiming the drop as a "move into".
        event.stopPropagation();
        setOver(`before:${subscription.feedId}`);
      }
    },
    onDrop: (event: React.DragEvent) => {
      if (
        dragged?.kind !== 'feed' ||
        // Dropping a feed on itself is a no-op, not a move to where it is.
        dragged.id === subscription.feedId ||
        props.onReorderFeed === undefined
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      props.onReorderFeed(dragged.id, subscription.folderId ?? null, subscription.feedId);

      setDragged(undefined);
      setOver(undefined);
    },
  });

  const renderFeed = (subscription: SubscriptionSummary): ReactNode => {
    const unread = unreadFor(subscription);

    return (
      <li key={subscription.feedId} {...reorderProps(subscription)}>
        <div
          className={`sidebar__feed${dragged?.kind === 'feed' && dragged.id === subscription.feedId ? ' is-dragging' : ''}${over === `before:${subscription.feedId}` ? ' is-drop-before' : ''}`}
          draggable={props.onMoveFeed !== undefined}
          onDragStart={(event) => {
            setDragged({ kind: 'feed', id: subscription.feedId });
            event.dataTransfer.effectAllowed = 'move';
            // Some browsers refuse a drag with no payload at all.
            event.dataTransfer.setData('text/plain', subscription.feedId);
          }}
          onDragEnd={() => {
            setDragged(undefined);
            setOver(undefined);
          }}
        >
          <button
            className={`sidebar__row${props.scope.feedId === subscription.feedId ? ' is-selected' : ''}${unread === 0 ? ' is-quiet' : ''}`}
            onClick={() => {
              props.onSelect({
                filter: feedFilter,
                feedId: subscription.feedId,
              });
            }}
          >
            <span className="sidebar__name">{subscription.title}</span>
            <HealthMarker health={subscription.health} title={subscription.title} />
            {props.showCounts && unread > 0 && (
              <span className="sidebar__count">{unread}</span>
            )}
          </button>

          {props.onMoveFeedDialog !== undefined && (
            <button
              className="sidebar__remove"
              aria-label={`Move ${subscription.title} to a folder`}
              title="Move to a folder"
              onClick={(event) => {
                event.stopPropagation();
                props.onMoveFeedDialog?.(subscription.feedId);
              }}
            >
              ⇄
            </button>
          )}

          {props.onUnsubscribe !== undefined && (
            /*
             * M2.5.1. Until this existed a feed added by mistake could not be
             * removed from the client at all, which is a worse first
             * impression than any missing feature — "I can't undo that" is
             * what makes people stop exploring.
             */
            <button
              className="sidebar__remove"
              aria-label={`Unsubscribe from ${subscription.title}`}
              title="Unsubscribe"
              onClick={(event) => {
                event.stopPropagation();
                props.onUnsubscribe?.(subscription.feedId, subscription.title);
              }}
            >
              ×
            </button>
          )}
        </div>
      </li>
    );
  };

  const renderFolder = (folder: Folder, depth: number): ReactNode => {
    const isCollapsed = props.collapsed.includes(folder.folderId);
    const unread = subtreeUnread(folder.folderId);
    const feeds = feedsIn(folder.folderId);
    const children = childrenOf(folder.folderId);

    return (
      <section
        className={`sidebar__folder${over === folder.folderId ? ' is-drop-target' : ''}${dragged?.kind === 'folder' && dragged.id === folder.folderId ? ' is-dragging' : ''}`}
        key={folder.folderId}
        style={{ '--depth': depth } as React.CSSProperties}
        {...dropProps(folder.folderId)}
      >
        <h2 className="sidebar__folder-heading">
          <span
            className="sidebar__folder-grip"
            draggable={props.onMoveFolder !== undefined}
            onDragStart={(event) => {
              setDragged({ kind: 'folder', id: folder.folderId });
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', folder.folderId);
            }}
            onDragEnd={() => {
              setDragged(undefined);
              setOver(undefined);
            }}
          >
            <button
              className="sidebar__row sidebar__row--folder"
              aria-expanded={!isCollapsed}
              onClick={() => {
                props.onToggleFolder(folder.folderId);
              }}
            >
              <span aria-hidden="true" className="sidebar__twisty">
                {isCollapsed ? '▸' : '▾'}
              </span>
              <span className="sidebar__name">{folder.name}</span>
              {props.showCounts && unread > 0 && (
                <span className="sidebar__count">{unread}</span>
              )}
            </button>
          </span>

          <button
            className="sidebar__scope"
            onClick={() => {
              props.onSelect({
                filter: feedFilter,
                folderId: folder.folderId,
              });
            }}
            title={`Show only ${folder.name}`}
          >
            open
          </button>
          {props.onRenameFolder !== undefined && (
            <button
              className="sidebar__scope"
              onClick={() => {
                props.onRenameFolder?.(folder.folderId);
              }}
              title="Rename this folder, or move it"
            >
              edit
            </button>
          )}
          {props.onDeleteFolder !== undefined && (
            <button
              className="sidebar__scope"
              onClick={() => {
                props.onDeleteFolder?.(folder.folderId);
              }}
              title="Delete this folder; its feeds stay subscribed"
            >
              delete
            </button>
          )}
        </h2>

        {!isCollapsed && (
          <>
            {children.map((child) => renderFolder(child, depth + 1))}
            {feeds.length > 0 && (
              <ul className="sidebar__list">{feeds.map(renderFeed)}</ul>
            )}
            {children.length === 0 && feeds.length === 0 && (
              <p className="sidebar__empty">Empty — drop a feed here.</p>
            )}
          </>
        )}
      </section>
    );
  };

  const unfiled = feedsIn(undefined);

  return (
    <nav className="sidebar" aria-label="Subscriptions">
      <div className="sidebar__header">
        <button className="button button--small" onClick={props.onAddFeed}>
          Add feed
        </button>
        {props.onCreateFolder !== undefined && (
          <button className="button button--small" onClick={props.onCreateFolder}>
            New folder
          </button>
        )}
        {props.onImport !== undefined && (
          <button className="button button--small" onClick={props.onImport}>
            Import
          </button>
        )}
      </div>

      <ul className="sidebar__list">
        <li>
          <button
            className={`sidebar__row${inFeeds && props.scope.folderId === undefined && props.scope.feedId === undefined ? ' is-selected' : ''}`}
            onClick={() => {
              props.onSelect({ filter: feedFilter });
            }}
          >
            <span className="sidebar__name">All feeds</span>
            {props.showCounts && props.total > 0 && (
              <span className="sidebar__count">{props.total}</span>
            )}
          </button>
        </li>
        <li>
          <button
            className={`sidebar__row${props.scope.filter === 'starred' ? ' is-selected' : ''}`}
            onClick={() => {
              props.onSelect({ filter: 'starred' });
            }}
          >
            <span className="sidebar__name">★ Starred</span>
          </button>
        </li>
      </ul>

      {/*
       * Tags (M4.2.3), across every feed and outliving them. Opens on "all",
       * because a tag is a collection somebody built, not an inbox to clear.
       */}
      {props.tags !== undefined && props.tags.length > 0 && (
        <section className="sidebar__folder" aria-label="Tags">
          <h2 className="sidebar__folder-heading sidebar__heading">Tags</h2>
          <ul className="sidebar__list">
            {props.tags.map((tag) => (
              <li key={tag.name}>
                <button
                  className={`sidebar__row${props.scope.tag === tag.name ? ' is-selected' : ''}`}
                  onClick={() => {
                    props.onSelect({ filter: 'all', tag: tag.name });
                  }}
                >
                  <span className="sidebar__name"># {tag.name}</span>
                  <span className="sidebar__count sidebar__count--quiet">
                    {tag.count}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {childrenOf(undefined).map((folder) => renderFolder(folder, 0))}

      {/*
       * The unfiled group is also a drop target, because "take this out of
       * that folder" needs somewhere to drop it — and it stays on screen even
       * when empty for exactly that reason.
       */}
      <section
        className={`sidebar__folder sidebar__folder--unfiled${over === UNFILED ? ' is-drop-target' : ''}`}
        {...dropProps(UNFILED)}
      >
        <h2 className="sidebar__folder-heading">
          <button
            className="sidebar__row sidebar__row--folder"
            aria-expanded={!props.collapsed.includes(UNFILED)}
            onClick={() => {
              props.onToggleFolder(UNFILED);
            }}
          >
            <span aria-hidden="true" className="sidebar__twisty">
              {props.collapsed.includes(UNFILED) ? '▸' : '▾'}
            </span>
            <span className="sidebar__name">Feeds</span>
            {props.showCounts &&
              unfiled.reduce((sum, feed) => sum + unreadFor(feed), 0) > 0 && (
                <span className="sidebar__count">
                  {unfiled.reduce((sum, feed) => sum + unreadFor(feed), 0)}
                </span>
              )}
          </button>
        </h2>

        {!props.collapsed.includes(UNFILED) &&
          (unfiled.length > 0 ? (
            <ul className="sidebar__list">{unfiled.map(renderFeed)}</ul>
          ) : (
            <p className="sidebar__empty">
              {props.folders.length > 0 ? 'Nothing outside a folder.' : 'No feeds yet.'}
            </p>
          ))}
      </section>
    </nav>
  );
}

/**
 * The at-a-glance half of M2.4.3.
 *
 * Three marks for three states, and **nothing at all for a healthy feed** —
 * including one that has not published since last year. A mark per row is a
 * list of problems; a mark on two rows out of forty is information.
 *
 * The tooltip is the server's sentence verbatim. Restating it here would
 * produce two wordings for one fact, and the server's is the one that knows
 * how long the feed has been failing.
 */
function HealthMarker(props: {
  readonly health:
    { readonly status: FeedHealthStatus; readonly detail: string | null } | undefined;
  readonly title: string;
}): ReactNode {
  const status = props.health?.status;

  if (status === undefined || status === 'ok') {
    return null;
  }

  const mark = status === 'dead' ? '⊘' : status === 'erroring' ? '!' : '·';

  return (
    <span
      className={`sidebar__health sidebar__health--${status}`}
      /*
       * A title attribute is not reachable from a keyboard or a screen
       * reader, so the accessible name carries the same information. The feed
       * detail banner (M2.4.3) is the version that does not need a pointer at
       * all.
       */
      title={props.health?.detail ?? `This feed is ${status}`}
      aria-label={`${props.title} is ${status}`}
      role="img"
    >
      {mark}
    </span>
  );
}

/** Position first, where the server gave one; name where it did not. */
function byPositionThenName(left: Folder, right: Folder): number {
  const a = left.position ?? Number.MAX_SAFE_INTEGER;
  const b = right.position ?? Number.MAX_SAFE_INTEGER;
  return a === b ? left.name.localeCompare(right.name) : a - b;
}

/** Whether `candidate` sits somewhere beneath `ancestor`. */
function isWithin(
  candidate: string,
  ancestor: string,
  folders: readonly Folder[],
): boolean {
  let current = folders.find((folder) => folder.folderId === candidate);
  let steps = 0;

  while (current !== undefined && steps <= folders.length) {
    const parentId = current.parentId ?? undefined;
    if (parentId === ancestor) {
      return true;
    }
    if (parentId === undefined) {
      return false;
    }
    current = folders.find((folder) => folder.folderId === parentId);
    steps += 1;
  }

  return false;
}
